import { dasherize } from "../utils/dom";
import Currency from "./currency";
import Ad from "./ad";
import FormatPrice from "./format-price";
import ListingThumb from "./listing-thumb";
import { AdFramework } from "./ad-framework";
import { feedAdAllowed } from "./ad-slot-budget";
import { useWindowedRow } from "../hooks/useWindowedRow";
import { useUI, useRouterContext } from "../contexts";
import { DATA_SOURCE } from "../data/js/data-sources";
import type { ListingPageType } from '../utils/listing-page-type';

/** A photo as the search/listing APIs return it — sized variants are optional. */
type ListingPhotoLike = {
  url: string;
  thumbUrl?: string;
  '320x320Url'?: string;
  '640x640Url'?: string;
};


// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ListingItem = Record<string, any>;

interface ListingsProps {
  list?: ListingItem[];
  setProduct: (item: ListingItem, listName?: string) => void;
  listName?: string;
  max?: number;
  clientSide?: boolean;
  tag?: string;
  searchQuery?: string;
  pageType?: ListingPageType;
  /**
   * Absolute position of this grid's first item -- the prerender split renders
   * items 0-3 separately, and without this both halves start at position 1.
   */
  positionOffset?: number;
  activeFilters?: Record<string, string>;
  /**
   * A look at the grid, not the grid itself (the Ranking tab's browse
   * preview): no ad tiles, and no result positions, so the cards record no
   * impressions. The caller blocks pointer events, which keeps hover tracking
   * and the product overlay out of it too.
   */
  preview?: boolean;
  /** Preview only: a strip under each card, given the card's listing and index. */
  renderFooter?: (item: ListingItem, index: number) => React.ReactNode;
}

interface ListItemProps {
  item: ListingItem;
  index: number;
  whichList?: string;
  setProduct: (item: ListingItem, listName?: string) => void;
  clientSide?: boolean;
  tag?: string;
  resultPosition?: number;
  listName?: string;
  totalResults?: number;
  searchQuery?: string;
  pageType?: string;
  activeFilters?: Record<string, string>;
}

const ListItem = ({
  item,
  index: _index,
  whichList: _whichList = "currentList",
  setProduct,
  clientSide: _clientSide,
  tag,
  // Hover tracking props
  resultPosition,
  listName,
  totalResults,
  searchQuery,
  pageType,
  activeFilters,
}: ListItemProps) => {
  const { setProductOpen } = useUI();
  const { push } = useRouterContext();
  const {
    id,
    photos,
    hasPhoto,
    title,
    price,
    priceMin,
    priceMax,
    dataSourceId,
    locationText,
    status,
    user = {},
    browse_url,
    salary_interval,
    itemCondition,
    slashedPrice,
    percentOff,
    categoryId,
  } = item;

  const isJob = item.browse_url && item.browse_url.match(/ziprecruiter/);

  const trustLevel = user && user.trustLevel ? parseInt(user.trustLevel) : 0;

  const productUrl = `/product/${id}/${dasherize(title) || "untitled"}/`;
  // Only a non-job card gets this handler; a job opens its own site in a new tab.
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Use router context to navigate to product page
    push(productUrl);
    setProduct(item);
    setProductOpen(true);
  };
  const url = isJob ? browse_url : productUrl;

  let amount;

  if (isJob) {
    if (price > 0) {
      amount = (
        <div>
          {Currency(price, FormatPrice)} {salary_interval}
        </div>
      );
    } else {
      amount = "";
    }
  } else if (
    typeof priceMin === "number" &&
    typeof priceMax === "number" &&
    priceMin < priceMax
  ) {
    // CJ multi-variant range
    amount = (
      <div data-testid="listing-price-range">
        {Currency(priceMin, FormatPrice)} – {Currency(priceMax, FormatPrice)}
      </div>
    );
  } else {
    if (price > 0) {
      amount = <div>{Currency(price, FormatPrice)}</div>;
    } else {
      amount =
        dataSourceId === DATA_SOURCE.RESCUEGROUPS ? (
          <div className="location-text">{locationText}</div>
        ) : (
          ""
        );
    }
  }

  const photo = getMainImage(item);
  const outgoing = isJob
    ? {
        target: "_blank",
        rel: "noreferrer noopener",
      }
    : {
        onClick,
      };
  return (
    <ListingThumb
      url={url}
      title={title}
      outgoing={outgoing}
      trustLevel={trustLevel}
      amount={amount}
      status={status}
      photo={photo}
      isJob={isJob}
      local={false}
      id={id}
      itemCondition={itemCondition}
      dataSourceId={dataSourceId}
      tag={tag}
      categoryId={categoryId}
      categoryGroupId={item.categoryGroupId}
      slashedPrice={slashedPrice}
      percentOff={percentOff}
      // Pass hover tracking props
      resultPosition={resultPosition}
      listName={listName}
      totalResults={totalResults}
      searchQuery={searchQuery}
      pageType={pageType as ListingPageType}
      activeFilters={activeFilters}
      // Tags reach the card so tag-grain engagement is computable; per-listing
      // rates will only ever be trustworthy for the head of the catalogue.
      tags={item.tags as string[] | undefined}
      photos={photos}
      locationText={locationText}
      hasPhoto={hasPhoto}
      featuredDate={item.featuredDate}
      copies={typeof item.copies === "number" ? item.copies : undefined}
    />
  );
};

/**
 * One grid cell that empties itself while it is far off screen.
 *
 * The grid feed appends tiles for as long as the reader scrolls and never
 * unmounted one, so every photo and every ad iframe it ever showed stayed in
 * the tab. On iPhone that is what the tab-crash sentinel caught on home and on
 * a seller page: the page had loaded, the same page came back seconds later,
 * and it was holding 234-292 images, 21-25 iframes and 7-9k DOM nodes.
 *
 * A parked cell keeps its box at the height it had, so the grid and the
 * scroll position do not move; the tile inside unmounts. An ad that unmounts
 * has its GAM slot destroyed (components/ad-slot-reaper.ts). Before the first
 * effect nothing is parked, so the server render still carries every tile.
 *
 * On Back a cell opens parked at the height it had (`keepKey`), and only the
 * cells near the screen mount their tile. Without it Back mounted every tile
 * the reader had scrolled past in one go and parked them a moment later: on
 * phones, Back to a search with 143-178 tiles held the main thread 5-7.5 s,
 * with 20-29 tiles under 1 s (prod, 2026-10-30).
 */
const WindowedCell = ({ className, keepKey, children }: { className: string; keepKey: string; children: React.ReactNode }) => {
  const { boxRef, parked } = useWindowedRow({ enabled: true, hasCards: true, keepKey });
  return (
    <div ref={boxRef} className={className}>
      {parked ? null : children}
    </div>
  );
};

/** Tiles between in-feed ads. The ad stands in place of the ninth listing. */
const AD_EVERY_N_TILES = 9;

const Listings = ({
  list = [],
  setProduct,
  listName,
  max = 200,
  clientSide,
  tag,
  // Hover tracking props
  searchQuery,
  pageType = 'browse_home',
  positionOffset = 0,
  activeFilters,
  preview = false,
  renderFooter,
}: ListingsProps) => {
  const l1 = list
    .map((item, i) => {
      if (i < max) return item;
    })
    .filter((a) => !!a);
  return l1.map((item, j) => {
      // Every ninth tile is an ad in place of a listing — but only while the
      // page budget can still pay for one. Past that `AdFramework` renders null
      // and this cell was left empty: a 250px hole, with the listing it
      // displaced dropped as well. Asking first means the feed just shows the
      // listing instead. The ordinal is stable across re-renders on purpose;
      // see components/ad-slot-budget.
      const isAdTile = j > 0 && (j + 1) % AD_EVERY_N_TILES === 0;
      const adOrdinal = (j + 1) / AD_EVERY_N_TILES - 1;
      if (!preview && isAdTile && feedAdAllowed(adOrdinal)) {
        // Keyed by the ad's position, not by the listing it stands in for. A
        // client-side navigation renders the previous page's list first and
        // swaps in the new results a moment later; keyed by listing id, every
        // ad then unmounted and remounted -- a second GAM slot, a second
        // auction, and a blank box while it ran. A hard load never saw it.
        return (
          <WindowedCell key={`ad-${adOrdinal}`} keepKey={`grid|${listName ?? ''}|ad-${adOrdinal}`} className="browse-ad-cont">
            <AdFramework
              bidstream={{
                mobile: (
                  <div
                    data-ad-tagid="wgw8vFZoalasLTuQSL4Q"
                    data-zid="1018"
                    data-name="SW - sellwild.com mobile 300x250"
                  />
                ),
                desktop: (
                  <div
                    data-ad-tagid="R7wZB7IZrKPO5EJAZTjq"
                    data-zid="1015"
                    data-name="SW - sellwild.com desktop 300x250"
                  />
                ),
              }}
              adsense={<Ad
                className="adsbygoogle"
                style={{ display: "block", margin: "auto", width: 300, height: 250 }}
                data-ad-client="ca-pub-3154552656014103"
                data-ad-slot="9551701632"
              />}
            />
          </WindowedCell>
      );
    }
    // `flex` so the card still stretches to the height of its grid row, as it
    // did when it was the grid item itself.
    //
    // `min-w-0` because the card is no longer the grid item. A grid item's
    // automatic minimum width is its content's -- unless it clips, and the card
    // (`.listing`) has `overflow: hidden`, so it never set one. This wrapper
    // does not clip: without `min-w-0` a card title on one line (573px) became
    // the phone grid's `1fr` column, and every phone showed 593px cards in a
    // 390px screen (2026-10-06, reverted; scripts/layout-gate now measures it).
    return (
      <WindowedCell key={item.id} keepKey={`grid|${listName ?? ''}|${item.id}`} className={renderFooter ? "flex min-w-0 flex-col" : "flex min-w-0"}>
        <ListItem
          item={item}
          index={j}
          setProduct={() => setProduct(item, listName)}
          clientSide={clientSide}
          tag={tag}
          // Pass hover tracking props with position
          // 1-indexed, absolute. None in a preview: no position, no impression.
          resultPosition={preview ? undefined : positionOffset + j + 1}
          totalResults={l1.length}
          listName={listName}
          searchQuery={searchQuery}
          pageType={pageType}
          activeFilters={activeFilters}
        />
        {renderFooter?.(item, j)}
      </WindowedCell>
    );
  });
};

/**
 * Main image for a grid card.
 *
 * Takes the 320px variant, not `url` — `url` is the 1280x1280 original, and a
 * screenful of those is megabytes of overdraw for thumbnail-sized tiles.
 * Foreign-hosted listings carry a single URL with no variants, so the chain
 * falls through to `url` and they render exactly as before.
 */
const getMainImage = ({ photos }: { photos?: ListingPhotoLike[] }) => {
  const first = (photos || [])[0];
  if (!first) return "";
  const src = first["320x320Url"] || first.thumbUrl || first.url;
  if (!src) return "";
  return src.includes("http") ? src : "https:" + src;
};

export default Listings;
